import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePatientRole } from '@/hooks/usePatientRole';
import { sanitizePatientData } from '@/hooks/useSensitivePatientData';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PatientDetailsCard } from '@/components/patient/PatientDetailsCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TaskFeedItem } from '@/components/dashboard/TaskFeedItem';

import { VitalSignsChart } from '@/components/dashboard/VitalSignsChart';
import { EventsChart } from '@/components/dashboard/EventsChart';
import { ProgressRing } from '@/components/ui/progress-ring';
import {
  Activity,
  Clock,
  Calendar,
  FileText,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  ShieldX,
  BarChart3,
  ClipboardList,
  AlertCircle,
  Zap,
  CheckCircle2,
  Navigation,
  History,
  LineChart,
  CalendarClock
} from 'lucide-react';
import { format, isToday, isYesterday, startOfDay, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import i18n from '@/i18n';
import { Loader2 } from 'lucide-react';

interface Patient {
  id: string;
  nickname: string;
  photo_url: string | null;
  birth_date: string | null;
  health_details: string | null;
  notes: string | null;
  residence_latitude: number | null;
  residence_longitude: number | null;
}

interface TaskExecution {
  id: string;
  executed_at: string;
  is_alert: boolean;
  is_late?: boolean;
  notes: string | null;
  photo_url: string | null;
  vital_value: number | null;
  vital_value_secondary: number | null;
  food_acceptance: string | null;
  is_sporadic: boolean;
  task: {
    name: string;
    task_type: string;
    vital_type?: string | null;
    unit?: string | null;
    incident_type?: string | null;
  };
  adhoc_task_type?: string | null;
  adhoc_name?: string | null;
  adhoc_vital_type?: string | null;
  executor?: {
    full_name: string | null;
    avatar_url?: string | null;
  } | null;
  // Preloaded counts to avoid N+1 queries
  reactions_count?: number;
  comments_count?: number;
  reactions?: Array<{ id: string; emoji: string; user_id: string }>;
}

interface Shift {
  id: string;
  check_in_at: string;
  check_out_at: string | null;
  status: string;
  caregiver_name?: string | null;
  tasks_completed?: number;
  tasks_total?: number;
  sporadic_count?: number;
  incident_count?: number;
  late_count?: number;
  adhoc_other_count?: number;
  check_in_latitude?: number | null;
  check_in_longitude?: number | null;
}

interface VitalData {
  vitalType: string;
  readings: { executed_at: string; vital_value: number; vital_value_secondary?: number | null }[];
  minAlertValue?: number | null;
  maxAlertValue?: number | null;
  unit?: string;
}

export default function FamilyDashboard() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const openComments = searchParams.get('openComments') === 'true';
  const highlightCommentId = searchParams.get('commentId');
  const urlTab = searchParams.get('tab');
  const { t } = useTranslation();
  const { canViewDashboard, isOwner, isLoading: roleLoading } = usePatientRole(patientId);

  const [patient, setPatient] = useState<Patient | null>(null);
  const [recentEvents, setRecentEvents] = useState<TaskExecution[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [activeShift, setActiveShift] = useState<Shift | null>(null);
  const [todayStats, setTodayStats] = useState({
    completed: 0,
    total: 0,
    alerts: 0,
    lateTasks: 0,
    adhocTasks: 0,
    adhocOther: 0
  });
  const [vitalData, setVitalData] = useState<VitalData[]>([]);
  const [eventsChartData, setEventsChartData] = useState<{ executed_at: string; is_alert: boolean }[]>([]);
  const [shiftTasks, setShiftTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(urlTab || 'timeline');

  // Feed pagination state
  const FEED_PAGE_SIZE = 20;
  const [feedOffset, setFeedOffset] = useState(0);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [highlightedItemLoaded, setHighlightedItemLoaded] = useState(false);
  const feedEndRef = useRef<HTMLDivElement>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);
  const [tabScrollHints, setTabScrollHints] = useState({ left: false, right: false });

  // Small arrows on the tab bar appear only while there are more tabs off-screen.
  const refreshTabScrollHints = useCallback(() => {
    const list = tabsListRef.current;
    if (!list) return;
    const maxScroll = list.scrollWidth - list.clientWidth;
    setTabScrollHints({
      left: list.scrollLeft > 4,
      right: list.scrollLeft < maxScroll - 4,
    });
  }, []);

  const scrollTabs = useCallback((direction: -1 | 1) => {
    const list = tabsListRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * Math.max(120, list.clientWidth * 0.7),
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    let timer = 0;
    let observer: ResizeObserver | null = null;
    const attempt = () => {
      if (cancelled || tries > 20) return;
      tries++;
      const list = tabsListRef.current;
      // The bar has no size yet (still mounting) — retry instead of reporting "nothing to scroll".
      if (!list || list.clientWidth === 0) {
        timer = window.setTimeout(attempt, 150);
        return;
      }
      refreshTabScrollHints();
      observer = new ResizeObserver(() => refreshTabScrollHints());
      observer.observe(list);
    };
    timer = window.setTimeout(attempt, 50);
    window.addEventListener('resize', refreshTabScrollHints);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      observer?.disconnect();
      window.removeEventListener('resize', refreshTabScrollHints);
    };
  }, [refreshTabScrollHints, activeTab]);

  // Handle tab change and update URL
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    setSearchParams(params => {
      params.set('tab', value);
      // Clear highlight params when switching away from timeline
      if (value !== 'timeline') {
        params.delete('highlight');
        params.delete('openComments');
        params.delete('commentId');
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  // Keep the active tab pill visible in the scrollable bar (deep links / back button)
  // Keep the active tab pill visible in the scrollable bar (deep links / back button).
  // Retries briefly because the bar may not be mounted yet on initial deep-link render.
  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    let timer = 0;
    const attempt = () => {
      if (cancelled || tries > 20) return;
      tries++;
      const list = tabsListRef.current;
      const active = list?.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
      if (!list || !active || list.clientWidth === 0) {
        timer = window.setTimeout(attempt, 150);
        return;
      }
      const listRect = list.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      const target = list.scrollLeft + (rect.left - listRect.left) - (list.clientWidth - rect.width) / 2;
      list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    };
    timer = window.setTimeout(attempt, 50);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [activeTab]);

  // Sync state with URL param if it changes externally (deep links / back button).
  // `activeTab` fica fora das dependências: incluí-lo gera um render extra a cada troca.
  useEffect(() => {
    if (urlTab) {
      setActiveTab(prev => (prev === urlTab ? prev : urlTab));
    }
  }, [urlTab]);


  // Force timeline tab when navigating from notification with highlight
  useEffect(() => {
    if (highlightId && activeTab !== 'timeline') {
      setActiveTab('timeline');
    }
  }, [highlightId, activeTab]);

  useEffect(() => {
    if (patientId) {
      fetchData();
    }
  }, [patientId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Fetch patient
      const { data: patientData } = await supabase
        .from('patients')
        .select('id, nickname, photo_url, birth_date, health_details, notes, residence_latitude, residence_longitude')
        .eq('id', patientId)
        .single();

      if (patientData) {
        setPatient(patientData as Patient);
      }

      // Fetch recent task executions with more details (Feed) - First page
      const { data: executions } = await supabase
        .from('task_executions')
        .select(`
          id,
          executed_at,
          is_alert,
          is_late,
          notes,
          photo_url,
          vital_value,
          vital_value_secondary,
          food_acceptance,
          is_sporadic,
          task_id,
          executed_by,
          adhoc_task_type,
          adhoc_name,
          adhoc_vital_type
        `)
        .eq('patient_id', patientId)
        .order('executed_at', { ascending: false })
        .range(0, FEED_PAGE_SIZE - 1);

      // Fetch vital signs data specifically for charts (last 30 days) - Bypass limit 50
      const thirtyDaysAgo = subDays(new Date(), 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

      // Fetch ALL executions for the events chart (last 30 days)
      const { data: allEventsRaw } = await supabase
        .from('task_executions')
        .select('executed_at, is_alert')
        .eq('patient_id', patientId)
        .gte('executed_at', thirtyDaysAgoStr)
        .order('executed_at', { ascending: true });

      setEventsChartData((allEventsRaw || []).map(e => ({
        executed_at: e.executed_at,
        is_alert: e.is_alert ?? false,
      })));

      const { data: vitalExecutionsRaw } = await supabase
        .from('task_executions')
        .select(`
          id,
          executed_at,
          vital_value,
          vital_value_secondary,
          task_id,
          adhoc_vital_type
        `)
        .eq('patient_id', patientId)
        .gte('executed_at', thirtyDaysAgoStr)
        .not('vital_value', 'is', null) // Only fetch records with values
        .order('executed_at', { ascending: true });

      // Fetch task details for both lists
      const taskIds = new Set<string>();
      const executorIds = new Set<string>();

      executions?.forEach(e => {
        if (e.task_id) taskIds.add(e.task_id);
        if (e.executed_by) executorIds.add(e.executed_by);
      });

      vitalExecutionsRaw?.forEach(e => {
        if (e.task_id) taskIds.add(e.task_id);
      });

      const [tasksResult, profilesResult] = await Promise.all([
        supabase
          .from('care_plan_tasks')
          .select('id, name, task_type, vital_type, unit, incident_type')
          .in('id', Array.from(taskIds)),
        supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url')
          .in('user_id', Array.from(executorIds))
      ]);

      const tasksMap = new Map((tasksResult.data || []).map(t => [t.id, t]));
      const profilesMap = new Map((profilesResult.data || []).map(p => [p.user_id, p]));

      // Fetch reactions and comments counts in batch for all executions
      const executionIds = (executions || []).map(e => e.id);
      const [reactionsResult, commentsResult] = await Promise.all([
        supabase
          .from('task_reactions')
          .select('task_execution_id, id, emoji, user_id')
          .in('task_execution_id', executionIds),
        supabase
          .from('task_comments')
          .select('task_execution_id')
          .in('task_execution_id', executionIds)
      ]);

      // Count reactions and comments per execution
      const reactionsCountMap = new Map<string, number>();
      const commentsCountMap = new Map<string, number>();
      const reactionsMap = new Map<string, Array<{ id: string; emoji: string; user_id: string }>>();

      reactionsResult.data?.forEach(r => {
        const currentCount = reactionsCountMap.get(r.task_execution_id) || 0;
        reactionsCountMap.set(r.task_execution_id, currentCount + 1);

        const currentReactions = reactionsMap.get(r.task_execution_id) || [];
        currentReactions.push({ id: r.id, emoji: r.emoji, user_id: r.user_id });
        reactionsMap.set(r.task_execution_id, currentReactions);
      });

      commentsResult.data?.forEach(c => {
        const current = commentsCountMap.get(c.task_execution_id) || 0;
        commentsCountMap.set(c.task_execution_id, current + 1);
      });

      // Enrich Feed Events
      const enrichedExecutions: TaskExecution[] = (executions || []).map(e => {
        const baseExecution = {
          reactions_count: reactionsCountMap.get(e.id) || 0,
          comments_count: commentsCountMap.get(e.id) || 0,
          reactions: reactionsMap.get(e.id) || []
        };

        // Handle ad-hoc tasks (task_id is null)
        if (!e.task_id) {
          return {
            ...e,
            ...baseExecution,
            adhoc_task_type: e.adhoc_task_type, // Ensure it's explicitly passed // Use explicitly passed type if adhoc // Fallback to 'other' if needed // This is safe because we filtered for ad hoc tasks // But let's be explicit

            task: {
              name: e.adhoc_name || 'Tarefa Avulsa',
              task_type: e.adhoc_task_type || 'other',
              vital_type: e.adhoc_vital_type,
              unit: null,
              incident_type: null
            },
            executor: profilesMap.get(e.executed_by) || null
          };
        }
        return {
          ...e,
          ...baseExecution,
          adhoc_task_type: e.adhoc_task_type,
          task: tasksMap.get(e.task_id) || { name: 'Tarefa', task_type: 'other' },
          executor: profilesMap.get(e.executed_by) || null
        };
      });

      setRecentEvents(enrichedExecutions);
      setFeedOffset(enrichedExecutions.length);
      setHasMoreFeed(enrichedExecutions.length >= FEED_PAGE_SIZE);

      // Process Chart Data
      const vitalExecutions: any[] = (vitalExecutionsRaw || []).map(e => {
        const taskDef = e.task_id ? tasksMap.get(e.task_id) : null;
        return {
          ...e,
          task: taskDef || {
            name: 'Avulso',
            task_type: e.task_id ? 'other' : 'vital_sign', // If ad-hoc and has vital value, treat as vital 
            vital_type: e.adhoc_vital_type
          }
        };
      }).filter(e => {
        return e.task.task_type === 'vital_sign' || !!e.task.vital_type;
      });

      // Group by vital type - fetch ranges from patient_vital_ranges
      const vitalTypesInData = [...new Set(vitalExecutions.map((e: any) => e.task?.vital_type).filter(Boolean))];
      let rangesMap = new Map<string, { min_value: number | null; max_value: number | null; unit: string | null }>();
      if (vitalTypesInData.length > 0) {
        const { data: rangesData } = await supabase
          .from('patient_vital_ranges')
          .select('vital_type, min_value, max_value, unit')
          .eq('patient_id', patientId!);
        if (rangesData) {
          rangesMap = new Map(rangesData.map(r => [r.vital_type, r]));
        }
      }

      const vitalGroups: Record<string, VitalData> = {};
      vitalExecutions.forEach((e: any) => {
        const vitalType = e.task.vital_type || 'unknown';
        if (!vitalGroups[vitalType]) {
          const rangeInfo = rangesMap.get(vitalType as any);

          vitalGroups[vitalType] = {
            vitalType,
            readings: [],
            minAlertValue: rangeInfo?.min_value,
            maxAlertValue: rangeInfo?.max_value,
            unit: rangeInfo?.unit || undefined
          };
        }

        vitalGroups[vitalType].readings.push({
          executed_at: e.executed_at,
          vital_value: e.vital_value!,
          vital_value_secondary: e.vital_value_secondary
        });
      });

      setVitalData(Object.values(vitalGroups));

      // Fetch shifts 
      const { data: shiftsData } = await supabase
        .from('shifts')
        .select('id, check_in_at, check_out_at, status, caregiver_id, check_in_latitude, check_in_longitude')
        .eq('patient_id', patientId)
        .order('check_in_at', { ascending: false })
        .limit(10);

      if (shiftsData && shiftsData.length > 0) {
        const caregiverIds = [...new Set(shiftsData.map(s => s.caregiver_id))];
        const shiftIds = shiftsData.map(s => s.id);

        const [profiles, shiftExecutions, shiftInstances] = await Promise.all([
          supabase
            .from('profiles')
            .select('user_id, full_name')
            .in('user_id', caregiverIds),
          supabase
            .from('task_executions')
            .select('id, shift_id, is_sporadic, task_id, is_alert, is_late, adhoc_task_type')
            .in('shift_id', shiftIds),
          supabase
            .from('shift_task_instances')
            .select('id, shift_id, status')
            .in('shift_id', shiftIds)
        ]);

        const profilesMap = new Map(profiles.data?.map(p => [p.user_id, p.full_name]) || []);

        // const scheduledExecutionCounts = new Map<string, number>(); // REMOVED: Caused duplicate counts
        const sporadicCounts = new Map<string, number>();
        const incidentCounts = new Map<string, number>();
        const adhocOtherCounts = new Map<string, number>();

        shiftExecutions.data?.forEach(exec => {
          if (exec.shift_id) {
            if (exec.adhoc_task_type === 'incident') {
              incidentCounts.set(exec.shift_id, (incidentCounts.get(exec.shift_id) || 0) + 1);
            } else if (exec.adhoc_task_type === 'sporadic_medication' || (exec.task_id && exec.is_sporadic)) {
              sporadicCounts.set(exec.shift_id, (sporadicCounts.get(exec.shift_id) || 0) + 1);
            } else if (!exec.task_id) {
              adhocOtherCounts.set(exec.shift_id, (adhocOtherCounts.get(exec.shift_id) || 0) + 1);
            }
            // else {
            //   scheduledExecutionCounts.set(exec.shift_id, (scheduledExecutionCounts.get(exec.shift_id) || 0) + 1);
            // }
          }
        });

        const instanceCounts = new Map<string, number>();
        const completedCounts = new Map<string, number>(); // ADDED: Count unique instances
        const lateCounts = new Map<string, number>();

        shiftInstances.data?.forEach(inst => {
          if (inst.shift_id) {
            instanceCounts.set(inst.shift_id, (instanceCounts.get(inst.shift_id) || 0) + 1);
            if (inst.status === 'completed') {
              completedCounts.set(inst.shift_id, (completedCounts.get(inst.shift_id) || 0) + 1);
            }
            if (inst.status === 'late') {
              lateCounts.set(inst.shift_id, (lateCounts.get(inst.shift_id) || 0) + 1);
            }
          }
        });

        // Tasks executed late (instance.status flips to 'completed' but execution.is_late=true)
        // must also be counted toward the late KPI.
        shiftExecutions.data?.forEach(exec => {
          if (exec.shift_id && exec.is_late) {
            lateCounts.set(exec.shift_id, (lateCounts.get(exec.shift_id) || 0) + 1);
          }
        });

        const enrichedShifts: Shift[] = shiftsData.map(s => ({
          ...s,
          caregiver_name: profilesMap.get(s.caregiver_id) || null,
          tasks_completed: completedCounts.get(s.id) || 0, // UPDATED: Use completed instances
          tasks_total: instanceCounts.get(s.id) || 0,
          sporadic_count: sporadicCounts.get(s.id) || 0,
          incident_count: incidentCounts.get(s.id) || 0,
          late_count: lateCounts.get(s.id) || 0,
          adhoc_other_count: adhocOtherCounts.get(s.id) || 0
        }));

        setShifts(enrichedShifts);

        const active = enrichedShifts.find(s => s.status === 'active');
        setActiveShift(active || null);

        if (active) {
          const { data: shiftInstances } = await supabase
            .from('shift_task_instances')
            .select(`
              id,
              scheduled_for,
              status,
              task_template:care_plan_tasks(id, name, task_type, vital_type, dose, dose_unit)
            `)
            .eq('shift_id', active.id)
            .order('scheduled_for', { ascending: true });

          const instances = shiftInstances || [];

          const { data: shiftExecutions } = await supabase
            .from('task_executions')
            .select('id, is_alert, is_late, is_sporadic, task_id, adhoc_task_type')
            .eq('shift_id', active.id);

          const executions = shiftExecutions || [];

          const completedCount = instances.filter(i => i.status === 'completed').length;
          const totalCount = instances.length;
          const lateCount =
            instances.filter(i => i.status === 'late').length +
            executions.filter(e => e.is_late).length;

          const incidentsCount = executions.filter(e => e.adhoc_task_type === 'incident').length;
          const sporadicMedsCount = executions.filter(e =>
            e.adhoc_task_type === 'sporadic_medication' ||
            (e.task_id && e.is_sporadic)
          ).length;
          const adhocOtherCount = executions.filter(e =>
            !e.task_id &&
            e.adhoc_task_type !== 'incident' &&
            e.adhoc_task_type !== 'sporadic_medication'
          ).length;

          setTodayStats({
            completed: completedCount,
            total: totalCount,
            alerts: incidentsCount,
            lateTasks: lateCount,
            adhocTasks: sporadicMedsCount,
            adhocOther: adhocOtherCount
          });

          setShiftTasks(instances);
        } else {
          setTodayStats({
            completed: 0,
            total: 0,
            alerts: 0,
            lateTasks: 0,
            adhocTasks: 0,
            adhocOther: 0
          });
          setShiftTasks([]);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load more feed items (pagination)
  const loadMoreFeed = useCallback(async () => {
    if (isFeedLoading || !hasMoreFeed || !patientId) return;

    setIsFeedLoading(true);
    try {
      const { data: executions } = await supabase
        .from('task_executions')
        .select(`
          id,
          executed_at,
          is_alert,
          is_late,
          notes,
          photo_url,
          vital_value,
          vital_value_secondary,
          food_acceptance,
          is_sporadic,
          task_id,
          executed_by,
          adhoc_task_type,
          adhoc_name,
          adhoc_vital_type
        `)
        .eq('patient_id', patientId)
        .order('executed_at', { ascending: false })
        .range(feedOffset, feedOffset + FEED_PAGE_SIZE - 1);

      if (!executions || executions.length === 0) {
        setHasMoreFeed(false);
        return;
      }

      // Get task and profile info for new batch
      const taskIds = new Set<string>();
      const executorIds = new Set<string>();
      executions.forEach(e => {
        if (e.task_id) taskIds.add(e.task_id);
        if (e.executed_by) executorIds.add(e.executed_by);
      });

      const [tasksResult, profilesResult] = await Promise.all([
        taskIds.size > 0 ? supabase
          .from('care_plan_tasks')
          .select('id, name, task_type, vital_type, unit, incident_type')
          .in('id', Array.from(taskIds)) : { data: [] },
        executorIds.size > 0 ? supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url')
          .in('user_id', Array.from(executorIds)) : { data: [] }
      ]);

      const tasksMap = new Map((tasksResult.data || []).map(t => [t.id, t]));
      const profilesMap = new Map((profilesResult.data || []).map(p => [p.user_id, p]));

      // Fetch reactions and comments counts in batch for new executions
      const executionIds = executions.map(e => e.id);
      const [reactionsResult, commentsResult] = await Promise.all([
        supabase
          .from('task_reactions')
          .select('task_execution_id, id, emoji, user_id')
          .in('task_execution_id', executionIds),
        supabase
          .from('task_comments')
          .select('task_execution_id')
          .in('task_execution_id', executionIds)
      ]);

      const reactionsCountMap = new Map<string, number>();
      const commentsCountMap = new Map<string, number>();
      const reactionsMap = new Map<string, Array<{ id: string; emoji: string; user_id: string }>>();

      reactionsResult.data?.forEach(r => {
        const currentCount = reactionsCountMap.get(r.task_execution_id) || 0;
        reactionsCountMap.set(r.task_execution_id, currentCount + 1);

        const currentReactions = reactionsMap.get(r.task_execution_id) || [];
        currentReactions.push({ id: r.id, emoji: r.emoji, user_id: r.user_id });
        reactionsMap.set(r.task_execution_id, currentReactions);
      });

      commentsResult.data?.forEach(c => {
        const current = commentsCountMap.get(c.task_execution_id) || 0;
        commentsCountMap.set(c.task_execution_id, current + 1);
      });

      const enrichedExecutions: TaskExecution[] = executions.map(e => {
        const baseExecution = {
          reactions_count: reactionsCountMap.get(e.id) || 0,
          comments_count: commentsCountMap.get(e.id) || 0,
          reactions: reactionsMap.get(e.id) || []
        };

        if (!e.task_id) {
          return {
            ...e,
            ...baseExecution,
            adhoc_task_type: e.adhoc_task_type,
            task: {
              name: e.adhoc_name || 'Tarefa Avulsa',
              task_type: e.adhoc_task_type || 'other',
              vital_type: e.adhoc_vital_type,
              unit: null,
              incident_type: null
            },
            executor: profilesMap.get(e.executed_by) || null
          };
        }
        return {
          ...e,
          ...baseExecution,
          adhoc_task_type: e.adhoc_task_type,
          task: tasksMap.get(e.task_id) || { name: 'Tarefa', task_type: 'other' },
          executor: profilesMap.get(e.executed_by) || null
        };
      });

      setRecentEvents(prev => [...prev, ...enrichedExecutions]);
      setFeedOffset(prev => prev + enrichedExecutions.length);
      setHasMoreFeed(enrichedExecutions.length >= FEED_PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more feed:', error);
    } finally {
      setIsFeedLoading(false);
    }
  }, [isFeedLoading, hasMoreFeed, patientId, feedOffset, FEED_PAGE_SIZE]);

  // Keep ref updated with latest function to avoid stale closure
  const loadMoreFeedRef = useRef(loadMoreFeed);
  loadMoreFeedRef.current = loadMoreFeed;

  // Intersection Observer for infinite scroll.
  // Ao voltar para a aba "Hoje" o observador é recriado; ignoramos o primeiro
  // disparo para não carregar mais uma página do feed sem o usuário rolar.
  useEffect(() => {
    const currentRef = feedEndRef.current;
    if (!currentRef || activeTab !== 'timeline') return;

    let armed = false;
    const arm = window.setTimeout(() => { armed = true; }, 300);

    const observer = new IntersectionObserver(
      (entries) => {
        if (!armed) return;
        if (entries[0].isIntersecting && !isFeedLoading) {
          loadMoreFeedRef.current();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(currentRef);
    return () => {
      window.clearTimeout(arm);
      observer.disconnect();
    };
  }, [activeTab, isFeedLoading]);

  // Fallback scroll listener for cases where IntersectionObserver doesn't work.
  // Só dispara em resposta a um scroll real do usuário.
  useEffect(() => {
    if (activeTab !== 'timeline') return;

    const handleScroll = () => {
      const currentRef = feedEndRef.current;
      if (!currentRef || isFeedLoading) return;

      const rect = currentRef.getBoundingClientRect();
      const isVisible = rect.top <= window.innerHeight + 200;

      if (isVisible) {
        loadMoreFeedRef.current();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab, isFeedLoading]);


  // Handle deep link - load specific item if not in current batch
  useEffect(() => {
    if (!highlightId || !patientId || highlightedItemLoaded) return;

    const itemExists = recentEvents.find(e => e.id === highlightId);
    if (itemExists) {
      setHighlightedItemLoaded(true);
      return;
    }

    // If loading is done but item isn't found, keep loading more
    if (!isLoading && hasMoreFeed && !isFeedLoading) {
      loadMoreFeed();
    }
  }, [highlightId, patientId, recentEvents, isLoading, hasMoreFeed, isFeedLoading, highlightedItemLoaded, loadMoreFeed]);

  const groupedEvents = useMemo(() => {
    const groups: Record<string, TaskExecution[]> = {};

    recentEvents.forEach(event => {
      const date = startOfDay(new Date(event.executed_at)).toISOString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });

    return Object.entries(groups).map(([date, items]) => ({
      date: new Date(date),
      items
    }));
  }, [recentEvents]);

  const formatDateHeader = (date: Date) => {
    if (isToday(date)) return t('familyDashboard.today');
    if (isYesterday(date)) return t('familyDashboard.yesterday');
    return format(date, "EEEE, d 'de' MMMM", { locale: i18n.language === 'pt-BR' ? ptBR : undefined });
  };


  if (isLoading || roleLoading) {
    return (
      <AppLayout title={t('familyDashboard.title')}>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!canViewDashboard) {
    return (
      <AppLayout title={t('familyDashboard.title')} showBack onBack={() => navigate(`/app/patients/${patientId}`)}>
        <div className="p-4">
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center">
                  <ShieldX className="w-8 h-8 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('familyDashboard.accessRestricted')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('familyDashboard.accessRestrictedDesc')}
                  </p>
                  <Button onClick={() => navigate(`/app/caregiver/${patientId}`)}>
                    {t('familyDashboard.goToCaregiverMode')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('familyDashboard.title')}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}`)}
      showNotifications
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Patient Header with Details - Sensitive data filtered by role */}
        <PatientDetailsCard
          patient={sanitizePatientData(patient, isOwner)}
          activeShift={activeShift}
          showHealthDetails={isOwner}
          showNotes={true}
          showLocationStatus={isOwner}
        />

        {/* Today's Stats - Standardized according to new definitions (7 metrics) */}
        <div className="space-y-3">
          {activeShift && (
            <div className="flex items-center gap-2 text-primary font-medium px-1">
              <Zap className="w-4 h-4" />
              <span className="text-sm">
                {t('shift.shiftPrefix')} {activeShift.caregiver_name || ''}
              </span>
            </div>
          )}
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <Card>
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-foreground">
                  {activeShift ? todayStats.total : '-'}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.plannedTasks')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-foreground">
                  {activeShift ? todayStats.completed : '-'}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.executedTasks')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-foreground">
                  {todayStats.total > 0
                    ? `${Math.round((todayStats.completed / todayStats.total) * 100)}%`
                    : '-'}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.completion')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-foreground">
                  {todayStats.adhocTasks}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.sporadicMeds')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className={`text-xl font-bold ${todayStats.alerts > 0 ? 'text-destructive' : 'text-foreground'}`}>
                  {todayStats.alerts}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.incidents')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className={`text-xl font-bold ${todayStats.lateTasks > 0 ? 'text-warning' : 'text-foreground'}`}>
                  {todayStats.lateTasks}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.lateTasks')}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-foreground">
                  {todayStats.adhocOther}
                </div>
                <p className="text-[12px] text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis">
                  {t('dashboard.adhocTasks')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="relative">
            <TabsList onScroll={refreshTabScrollHints} ref={tabsListRef} className="flex h-auto w-full items-center justify-start gap-2 overflow-x-auto scrollbar-none rounded-md bg-muted p-1">
            <TabsTrigger value="timeline" className="flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4">
              <History className="w-4 h-4" />
              <span>{t('dashboard.tabs.feed')}</span>
            </TabsTrigger>
            <TabsTrigger value="pending" className="relative flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4">
              <ClipboardList className="w-4 h-4" />
              <span>{t('dashboard.tabs.activities')}</span>
              {shiftTasks.filter(t => t.status === 'late').length > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {shiftTasks.filter(t => t.status === 'late').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="vitals" className="flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4">
              <LineChart className="w-4 h-4" />
              <span>{t('dashboard.tabs.charts')}</span>
            </TabsTrigger>
            <TabsTrigger value="shifts" className="flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4">
              <CalendarClock className="w-4 h-4" />
              <span>{t('dashboard.tabs.shifts')}</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4">
              <FileText className="w-4 h-4" />
              <span>{t('dashboard.tabs.reports')}</span>
            </TabsTrigger>
            </TabsList>

            {tabScrollHints.left && (
              <button
                type="button"
                aria-label={t('dashboard.tabs.scrollLeft')}
                onClick={() => scrollTabs(-1)}
                className="absolute inset-y-1 left-0 z-10 flex w-6 items-center justify-center rounded-l-md bg-gradient-to-r from-muted from-40% to-transparent text-muted-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {tabScrollHints.right && (
              <button
                type="button"
                aria-label={t('dashboard.tabs.scrollRight')}
                onClick={() => scrollTabs(1)}
                className="absolute inset-y-1 right-0 z-10 flex w-6 items-center justify-center rounded-r-md bg-gradient-to-l from-muted from-40% to-transparent text-muted-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Section title for the active tab */}
          <h2 className="mt-4 text-base font-semibold">{t(`dashboard.tabTitles.${activeTab}`)}</h2>

          {/* Timeline Tab - Instagram-style Feed */}
          <TabsContent value="timeline" forceMount className={activeTab === 'timeline' ? "mt-4 space-y-4" : "hidden"}>
            {groupedEvents.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('familyDashboard.noEvents')}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {groupedEvents.map(group => (
                <div key={group.date.toISOString()}>

                  <div className="space-y-3">
                    {group.items.map(event => (
                      <TaskFeedItem
                        key={event.id}
                        execution={event}
                        patientId={patientId}
                        isOwner={isOwner}
                        isHighlighted={event.id === highlightId}
                        autoOpenComments={openComments && event.id === highlightId}
                        highlightCommentId={event.id === highlightId ? highlightCommentId : undefined}
                      />
                    ))}
                  </div>
                </div>
                ))}
              </>
            )}


            {/* Infinite scroll trigger */}
            <div ref={feedEndRef} className="py-4">
              {isFeedLoading && (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">{t('common.loading')}</span>
                </div>
              )}
              {!isFeedLoading && hasMoreFeed && recentEvents.length > 0 && (
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={loadMoreFeed}
                >
                  {t('familyDashboard.loadMore')}
                </Button>
              )}
              {!hasMoreFeed && recentEvents.length > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  {t('familyDashboard.noMoreEvents')}
                </p>
              )}
            </div>
          </TabsContent>

          {/* Pending/Late/Executed Tasks Tab */}
          <TabsContent value="pending" forceMount className={activeTab === 'pending' ? "mt-4 space-y-4" : "hidden"}>
            {!activeShift ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('familyDashboard.noActiveShift')}</p>
                </CardContent>
              </Card>
            ) : shiftTasks.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('familyDashboard.noPendingTasks')}</p>
                  <p className="text-xs mt-2">{t('familyDashboard.allTasksCompleted')}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Late Tasks */}
                {shiftTasks.filter(t => t.status === 'late').length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-destructive mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('familyDashboard.late')} ({shiftTasks.filter(t => t.status === 'late').length})
                    </h3>
                    <div className="space-y-2">
                      {shiftTasks
                        .filter(t => t.status === 'late')
                        .map(instance => (
                          <Card key={instance.id} className="border-destructive/50 bg-destructive/5">
                            <CardContent className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                                  <AlertCircle className="w-5 h-5 text-destructive" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">{instance.task_template?.name || 'Tarefa'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Agendada para {format(new Date(instance.scheduled_for), 'HH:mm')}
                                    {instance.task_template?.dose && ` • ${instance.task_template.dose} ${instance.task_template.dose_unit}`}
                                  </p>
                                </div>
                                <Badge variant="destructive" className="text-[12px]">
                                  Atrasada
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                    </div>
                  </div>
                )}

                {/* Pending Tasks */}
                {shiftTasks.filter(t => t.status === 'pending').length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-warning mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t('familyDashboard.pending')} ({shiftTasks.filter(t => t.status === 'pending').length})
                    </h3>
                    <div className="space-y-2">
                      {shiftTasks
                        .filter(t => t.status === 'pending')
                        .map(instance => (
                          <Card key={instance.id}>
                            <CardContent className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                  <Clock className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">{instance.task_template?.name || 'Tarefa'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Agendada para {format(new Date(instance.scheduled_for), 'HH:mm')}
                                    {instance.task_template?.dose && ` • ${instance.task_template.dose} ${instance.task_template.dose_unit}`}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-[12px]">
                                  Pendente
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                    </div>
                  </div>
                )}

                {/* Executed Tasks */}
                {
                  shiftTasks.filter(t => t.status === 'completed').length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-success mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        {t('familyDashboard.executed')} ({shiftTasks.filter(t => t.status === 'completed').length})
                      </h3>
                      <div className="space-y-2">
                        {shiftTasks
                          .filter(t => t.status === 'completed')
                          .map(instance => (
                            <Card key={instance.id} className="border-success/50 bg-success/5">
                              <CardContent className="p-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-success" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium">{instance.task_template?.name || 'Tarefa'}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Agendada para {format(new Date(instance.scheduled_for), 'HH:mm')}
                                      {instance.task_template?.dose && ` • ${instance.task_template.dose} ${instance.task_template.dose_unit}`}
                                    </p>
                                  </div>
                                  <Badge variant="default" className="text-[12px] bg-success hover:bg-success/90">
                                    Executada
                                  </Badge>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    </div>
                  )
                }
              </>
            )}
          </TabsContent>

          {/* Vitals Tab - Charts */}
          <TabsContent value="vitals" forceMount className={activeTab === 'vitals' ? "mt-4 space-y-4" : "hidden"}>
            {/* Events Chart */}
            <EventsChart executions={eventsChartData} />

            {/* Vital Signs Charts */}
            {vitalData.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('familyDashboard.noVitalsData')}</p>
                </CardContent>
              </Card>
            ) : (
              vitalData.map(data => (
                <VitalSignsChart
                  key={data.vitalType}
                  vitalType={data.vitalType}
                  readings={data.readings}
                  minAlertValue={data.minAlertValue}
                  maxAlertValue={data.maxAlertValue}
                  unit={data.unit}
                />
              ))
            )}
          </TabsContent>

          {/* Shifts Tab */}
          <TabsContent value="shifts" forceMount className={activeTab === 'shifts' ? "mt-4 space-y-3" : "hidden"}>
            {shifts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('familyDashboard.noShifts')}</p>
                </CardContent>
              </Card>
            ) : (
              shifts.map(shift => {
                const completionPercentage = shift.tasks_total && shift.tasks_total > 0
                  ? Math.round((shift.tasks_completed || 0) / shift.tasks_total * 100)
                  : 0;

                return (
                  <Card
                    key={shift.id}
                    className={`cursor-pointer hover:bg-accent/50 transition-colors ${shift.status === 'active' ? 'border-primary' : ''
                      }`}
                    onClick={() => navigate(`/app/family/${patientId}/shift/${shift.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`p-2 rounded-full shrink-0 ${shift.status === 'active'
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                            }`}>
                            <Clock className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold leading-tight">
                                {format(new Date(shift.check_in_at), 'dd/MM/yyyy')}
                              </p>
                              {shift.status === 'active' && (
                                <Badge variant="default" className="text-[12px]">{t('familyDashboard.active')}</Badge>
                              )}
                            </div>
                            <p className="text-[13px] md:text-sm text-muted-foreground">
                              {shift.caregiver_name || 'Cuidador'} • {format(new Date(shift.check_in_at), 'HH:mm')}
                              {shift.check_out_at && ` - ${format(new Date(shift.check_out_at), 'HH:mm')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <ProgressRing progress={completionPercentage} size={40} strokeWidth={4}>
                            <span className="text-xs font-medium">{completionPercentage}%</span>
                          </ProgressRing>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-y-3 md:gap-y-1 gap-x-2 md:gap-x-3 mt-3 pt-3 border-t">
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <ClipboardList className="w-3.5 h-3.5 md:w-3 md:h-3 text-primary shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.plannedTasks')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.tasks_total || 0}</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 md:w-3 md:h-3 text-success shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.executedTasks')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.tasks_completed || 0}</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5 md:w-3 md:h-3 text-sporadic shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.sporadicShort')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.sporadic_count || 0}</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 md:w-3 md:h-3 text-destructive shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.incidents')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.incident_count || 0}</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 md:w-3 md:h-3 text-warning shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.lateTasks')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.late_count || 0}</span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Activity className="w-3.5 h-3.5 md:w-3 md:h-3 text-muted-foreground shrink-0" />
                            <span className="text-[11px] md:text-[12px] text-muted-foreground leading-tight whitespace-nowrap">
                              {t('dashboard.adhocTasks')}<span className="hidden md:inline">:</span>
                            </span>
                          </div>
                          <span className="text-sm md:text-[12px] font-semibold md:font-normal leading-tight md:pl-0.5">{shift.adhoc_other_count || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" forceMount className={activeTab === 'reports' ? "mt-4 space-y-3" : "hidden"}>
            <Card
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => navigate(`/app/family/${patientId}/report/daily`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10 text-primary">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">Relatório Diário</p>
                      <p className="text-sm text-muted-foreground">
                        Resumo das últimas 24 horas
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => navigate(`/app/family/${patientId}/report/weekly`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-secondary/10 text-secondary-foreground">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">Relatório Semanal</p>
                      <p className="text-sm text-muted-foreground">
                        Resumo dos últimos 7 dias
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => navigate(`/app/family/${patientId}/report/medical-30-days`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-sporadic/10 text-sporadic">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium">Relatório médico - 30 dias</p>
                      <p className="text-sm text-muted-foreground">
                        Visão clínica completa
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
